/**
 * The select-only PEEK at the conversation list, shared by the collapsed rail's + button (APP-3397) and the
 * dock header's ☰ (APP-3443). Quick-jump only — Pinned/Recents rows, no search and no rename/pin/delete; the
 * caller's click affordance is what opens the full list. Selecting navigates and dismisses.
 *
 * Two things are deliberate. It carries `PANEL_WIDTH`, so the peek and the panel it previews are one width. And
 * it is a `Popper`, which portals to `document.body`: inside the dock's transformed box `position: fixed` is
 * `absolute`, so a portal is the only way a floating surface escapes the dock's own bounds and z-index.
 */

import { useId, useMemo } from "react"
import type { FocusEvent, Ref } from "react"
import { Alert, Box, CircularProgress, Paper, Popper, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { THREAD_LIST_SURFACES } from "connected-components/ai-teammate/analytics/events"
import { PANEL_WIDTH, ThreadSection } from "./side-panel"
import type { ThreadRowCallbacks } from "./thread-list-item"
import { partitionThreads } from "./thread-partition"
import { useKellyPanel } from "./kelly-chat-context"

// tooltip zIndex keeps the peek above the sticky app bar AND above the dock (1105), which it anchors inside of.
const POPPER_SX = { zIndex: "tooltip" } satisfies SxProps<Theme>
const PREVIEW_PAPER_SX = { width: PANEL_WIDTH, maxHeight: "70vh", display: "flex", flexDirection: "column", borderRadius: "6px", boxShadow: 8, overflow: "hidden" } satisfies SxProps<Theme>
const LISTS_SCROLL_SX = { flex: 1, overflowY: "auto", minHeight: 0 } satisfies SxProps<Theme>
const ALERT_SX = { m: 1 } satisfies SxProps<Theme>
const LOADING_SX = { display: "flex", justifyContent: "center", p: 2 } satisfies SxProps<Theme>
const HINT_SX = { m: 1, px: 1, py: 0.75, borderRadius: "4px", backgroundColor: "var(--app-subtle-hover-surface)", color: "var(--app-text-secondary)", fontSize: "0.6875rem" } satisfies SxProps<Theme>

type ThreadsStatus = "loading" | "loaded" | "errored"

export interface HoverPreviewSurfaceHandlers {
  onMouseEnter: () => void
  onMouseLeave: () => void
  onBlur: (event: FocusEvent<HTMLDivElement>) => void
}

export interface ConversationsPreviewProps {
  isOpen: boolean
  /** The trigger. State rather than a ref in every caller: reading a ref during render is forbidden here. */
  anchorEl: HTMLElement | null
  surfaceRef: Ref<HTMLDivElement>
  surfaceHandlers: HoverPreviewSurfaceHandlers
  /** Dismiss after a select — the caller's `closeNow`, so the peek never outlives the navigation it caused. */
  onDismiss: () => void
  /** Root test id; the loading and error branches derive theirs from it, so each caller stays addressable. */
  testId: string
}

export function ConversationsPreview({ isOpen, anchorEl, surfaceRef, surfaceHandlers, onDismiss, testId }: ConversationsPreviewProps) {
  const { threads, activeThreadId, isThreadsLoaded, isThreadsErrored, selectThread } = useKellyPanel()
  const headingId = useId()

  const callbacks = useMemo<ThreadRowCallbacks>(
    () => ({ onSelect: (threadId) => { onDismiss(); selectThread(threadId) } }),
    [onDismiss, selectThread],
  )

  const { pinned, recents } = useMemo(() => partitionThreads(threads), [threads])
  // One derived status so the error / loading / list branches are mutually exclusive (mirrors the side panel's guard).
  const threadsStatus: ThreadsStatus = isThreadsErrored ? "errored" : isThreadsLoaded ? "loaded" : "loading"

  return (
    <Popper open={isOpen && anchorEl != null} anchorEl={anchorEl} placement="bottom-start" sx={POPPER_SX}>
      <Paper ref={surfaceRef} sx={PREVIEW_PAPER_SX} data-testid={testId} {...surfaceHandlers}>
        {threadsStatus === "errored" && (
          <Alert severity="error" sx={ALERT_SX} data-testid={`${testId}-error`}>
            Couldn&apos;t load your conversations. Please try again later.
          </Alert>
        )}
        {threadsStatus === "loading" && (
          <Box sx={LOADING_SX} data-testid={`${testId}-loading`}>
            <CircularProgress size={20} aria-label="Loading conversations" />
          </Box>
        )}
        {threadsStatus === "loaded" && (
          <Box sx={LISTS_SCROLL_SX}>
            {pinned.length > 0 && (
              <ThreadSection
                surface={THREAD_LIST_SURFACES.RECENTS_PREVIEW}
                headingId={`${headingId}-pinned`}
                label="Pinned"
                source="pinned"
                threads={pinned}
                count={pinned.length}
                activeThreadId={activeThreadId}
                callbacks={callbacks}
                variant="preview"
              />
            )}
            {recents.length > 0 && (
              <ThreadSection
                surface={THREAD_LIST_SURFACES.RECENTS_PREVIEW}
                headingId={`${headingId}-recents`}
                label="Recents"
                source="recents"
                threads={recents}
                count={recents.length}
                activeThreadId={activeThreadId}
                callbacks={callbacks}
                variant="preview"
              />
            )}
          </Box>
        )}
        <Typography sx={HINT_SX}>Select a conversation to open it</Typography>
      </Paper>
    </Popper>
  )
}
